import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CloudOff, Eye, EyeOff, ListOrdered, Lock, Rotate3d, Undo2 } from 'lucide-react';
import { useTeamStore } from '../../../../store/teamStore';
import { useFeatureAccess } from '../../../../hooks/useFeatureAccess';
import { useWakeLock } from '../../../../hooks/useWakeLock';
import { subscriptionService } from '../../../../services/subscriptionService';
import { formatGamePoint, reduce, type MatchState, type PointPressure, type Side } from '../../../../services/tennis/scoring';
import { useTennisScoringMatch } from './useTennisScoringMatch';
import { PointDetailsSheet } from './PointDetailsSheet';
import { MatchSummaryOverlay } from './MatchSummaryOverlay';
import { ScoreBoard } from './ScoreBoard';
import { ServeControls } from './ServeControls';
import { PointLogSheet, PointLogStrip } from './PointLog';
import { TennisBall } from './TennisBall';
import { loadQuickTagAutoSave } from './scoringPreferences';
import UpgradeModal from '../../../UpgradeModal';

interface SideStats {
  points: number;
  firstServePct: number | null;
  aces: number;
  doubleFaults: number;
  winners: number;
  unforcedErrors: number;
}

/**
 * How much the post-point drawer asks for. Hardcoded to 'quick' for now —
 * planned to become a team setting (Score only / Quick tags / Full detail,
 * plus per-field toggles) so score-only coaches never see the drawer and
 * detail-focused ones get it pre-expanded.
 */
type PointDetailLevel = 'score_only' | 'quick' | 'full';
const POINT_DETAIL_LEVEL = 'quick' as PointDetailLevel;

const DARK_MODE_STORAGE_KEY = 'hustli_tennis_scoring_dark_mode';

const PRESSURE_LABEL: Record<PointPressure, string> = {
  match: 'Match point',
  set: 'Set point',
  break: 'Break point',
  game: 'Game point',
};

function loadDarkModePreference(): boolean {
  try {
    return window.localStorage.getItem(DARK_MODE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function sideGameScore(state: MatchState, side: Side, noAd: boolean): string {
  if (!state.game) return '–';
  const display = state.game.isTB
    ? { ours: String(state.game.points.ours), theirs: String(state.game.points.theirs) }
    : formatGamePoint(state.game.points, noAd);
  return side === 'us' ? display.ours : display.theirs;
}

/** The winning side's score before → after the point just played, e.g. "30 → 40", "40 → Game", "AD → Set". */
function describeScoreDelta(prev: MatchState, next: MatchState, side: Side, noAd: boolean): { from: string; to: string } | null {
  if (!prev.game) return null;
  const from = sideGameScore(prev, side, noAd);
  if (next.winner) return { from, to: 'Match' };
  if (next.sets.length > prev.sets.length) return { from, to: 'Set' };
  const gamesPlayed = (s: MatchState) => (s.currentSet?.ourGames ?? 0) + (s.currentSet?.theirGames ?? 0);
  if (gamesPlayed(next) !== gamesPlayed(prev)) return { from, to: 'Game' };
  const g = next.game;
  if (g && !g.isTB && !noAd && g.points.ours >= 3 && g.points.ours === g.points.theirs) return { from, to: 'Deuce' };
  return { from, to: sideGameScore(next, side, noAd) };
}

/**
 * Mobile-first live scoring pad (sc-338 / Tennis Phase 4). Full-screen, no
 * app chrome — a coach or player needs this usable with one thumb, standing,
 * without staring at it between points. Layout ("Split Court"): each side's
 * half of the screen is its point button, opponent on top and our side
 * nearest the thumb, with serve controls between them.
 */
export const TennisScoringScreen: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { currentTeam } = useTeamStore();
  const { checkFeatureAccess, isProUser, profile, showUpgradeModal, handleUpgrade, handleCloseModal } = useFeatureAccess();

  const {
    loading,
    game,
    format,
    isDoubles,
    ourPlayers,
    points,
    state,
    serveState,
    online,
    storageAvailable,
    unsyncedCount,
    netPlayerId,
    toggleNetPlayer,
    tapWon,
    recordFault,
    tagLastPoint,
    undo,
  } = useTennisScoringMatch(gameId);

  const [showDetailsSheet, setShowDetailsSheet] = useState(false);
  const [showPointLog, setShowPointLog] = useState(false);
  const [lastWonBy, setLastWonBy] = useState<Side | null>(null);
  const [statsSide, setStatsSide] = useState<Side>('us');
  const [darkMode, setDarkMode] = useState(loadDarkModePreference);
  // Read once per visit — changed from Settings → Match Scoring, not mid-match.
  const [autoSave] = useState(loadQuickTagAutoSave);

  useWakeLock(!loading && !state.winner);

  // Scoped to <html> rather than this screen's own root: BottomSheet/MatchSummaryOverlay
  // render via createPortal(document.body), which escapes this component's DOM subtree,
  // so a `.dark` class here wouldn't reach them. Toggling on <html> covers portaled
  // content too, and nothing else in the app uses the `dark:` variant, so it's a safe,
  // fully-scoped-in-practice toggle. Always cleaned up on unmount.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    try {
      window.localStorage.setItem(DARK_MODE_STORAGE_KEY, String(darkMode));
    } catch {
      // Private browsing / storage unavailable — dark mode just won't persist across visits.
    }
    return () => {
      document.documentElement.classList.remove('dark');
    };
  }, [darkMode]);

  const ourName = useMemo(() => {
    if (ourPlayers.length === 0) return currentTeam?.name ?? 'Us';
    if (!isDoubles) return `${ourPlayers[0].firstName} ${ourPlayers[0].lastName}`;
    return ourPlayers.map((p) => p.firstName).join(' / ');
  }, [ourPlayers, isDoubles, currentTeam?.name]);

  const opponentName = game?.opponent_name || 'Opponent';

  // Doubles: server is "whichever of our two players isn't at net" — the net switch
  // controls both, since they're complementary (see useTennisScoringMatch's note).
  const serverName = useMemo(() => {
    if (state.server === 'them') return opponentName;
    if (!isDoubles) return ourName;
    const server = ourPlayers.find((p) => p.id !== netPlayerId);
    return server ? server.firstName : ourName;
  }, [state.server, isDoubles, ourPlayers, netPlayerId, ourName, opponentName]);

  const netPlayerName = useMemo(() => {
    if (!isDoubles) return null;
    return ourPlayers.find((p) => p.id === netPlayerId)?.firstName ?? null;
  }, [isDoubles, ourPlayers, netPlayerId]);

  /**
   * Key live stats for each side, derived from the point log — never stored
   * (Phase 5 owns the real sport_stats projection).
   */
  const matchStats = useMemo(() => {
    const blank = (): SideStats => ({ points: 0, firstServePct: null, aces: 0, doubleFaults: 0, winners: 0, unforcedErrors: 0 });
    const stats: Record<Side, SideStats> = { us: blank(), them: blank() };
    const serveAttempts: Record<Side, number> = { us: 0, them: 0 };
    const serveFirstIn: Record<Side, number> = { us: 0, them: 0 };

    for (const p of points) {
      stats[p.won_by].points++;

      if (p.serve_outcome) {
        serveAttempts[p.server]++;
        if (p.serve_outcome === '1st_in') serveFirstIn[p.server]++;
        if (p.serve_outcome === 'double_fault') stats[p.server].doubleFaults++;
      }
      if (p.end_reason === 'ace') stats[p.won_by].aces++;
      if (p.end_reason === 'winner') stats[p.won_by].winners++;
      if (p.end_reason === 'unforced') stats[p.won_by === 'us' ? 'them' : 'us'].unforcedErrors++;
    }

    (['us', 'them'] as const).forEach((side) => {
      stats[side].firstServePct = serveAttempts[side] > 0 ? Math.round((serveFirstIn[side] / serveAttempts[side]) * 100) : null;
    });

    return stats;
  }, [points]);

  const lastPoint = points.length > 0 ? points[points.length - 1] : null;

  // Only computed while the drawer is up — replays the log minus the last point to get the "before" score.
  const scoreDelta = useMemo(() => {
    if (!showDetailsSheet || !lastWonBy || points.length === 0) return null;
    const prev = reduce(format, points.slice(0, -1).map((p) => ({ winner: p.won_by })), { isDoubles });
    return describeScoreDelta(prev, state, lastWonBy, format.noAd);
  }, [showDetailsSheet, lastWonBy, points, format, isDoubles, state]);

  const handleTap = (side: Side) => {
    tapWon(side);
    setLastWonBy(side);
    if (POINT_DETAIL_LEVEL !== 'score_only') setShowDetailsSheet(true);
  };

  const handleSaveDetails = (updates: Parameters<typeof tagLastPoint>[0]) => {
    tagLastPoint(updates);
    setShowDetailsSheet(false);
  };

  const handleSkipDetails = () => {
    setShowDetailsSheet(false);
  };

  const handleRequirePro = () => {
    checkFeatureAccess('Advanced point tagging');
  };

  if (loading || !game) {
    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-gray-950 flex items-center justify-center">
        <p className="text-gray-500 dark:text-gray-400 text-sm">Loading match…</p>
      </div>
    );
  }

  // Rookie: 1 match started/month (Pro is uncapped). Authoritative gate — this route can be
  // reached directly (bookmark, browser back/forward cache), so TennisMatchStatusCard's
  // pre-navigation check alone isn't enough. A match that's already live/complete/abandoned
  // was already counted (or predates the limit) and must never be blocked from resuming —
  // only a fresh, not-yet-started match can be turned away here.
  const matchAlreadyStarted = game.match_state === 'live' || game.match_state === 'complete' || game.match_state === 'abandoned';
  if (!matchAlreadyStarted && subscriptionService.hasReachedTennisScoringLimit(profile)) {
    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-gray-950 flex flex-col items-center justify-center gap-4 p-6 text-center">
        <Lock className="w-10 h-10 text-gray-300 dark:text-gray-700" />
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Monthly match limit reached</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
            Rookie accounts can live-score 1 Tennis match per month. Upgrade to Pro for unlimited live scoring.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => navigate(`/games/${gameId}`)} className="btn-secondary text-sm">
            Back
          </button>
          <button type="button" onClick={handleUpgrade} className="btn-primary text-sm">
            Upgrade to Pro
          </button>
        </div>
      </div>
    );
  }

  const setNumber = state.sets.length + 1;
  const gameNumber = (state.currentSet?.ourGames ?? 0) + (state.currentSet?.theirGames ?? 0) + 1;
  const isMatchTiebreak = state.isDeciding && format.sets > 1 && format.decidingSet === 'match_tiebreak';
  const title = state.winner
    ? 'Match complete'
    : state.game?.isTB
    ? isMatchTiebreak
      ? 'Match tiebreak'
      : `Set ${setNumber} · Tiebreak`
    : `Set ${setNumber} · Game ${gameNumber}`;
  const showDecidingChip = state.isDeciding && format.sets > 1 && format.decidingSet !== 'match_tiebreak';

  return (
    <div
      className="fixed inset-0 z-50 bg-[#F7F5F0] dark:bg-gray-950 flex flex-col"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* Header: title bar, set scoreboard, last-10 strip */}
      <div className="flex-shrink-0 px-3 pt-1.5 space-y-1.5">
        <div className="flex items-center gap-2 min-h-[44px]">
          <button
            type="button"
            onClick={() => navigate(`/games/${gameId}`)}
            aria-label="Exit scoring"
            className="p-2 -ml-2 rounded-full active:bg-gray-200 dark:active:bg-gray-800"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>
          <span className="flex-1 min-w-0 truncate text-sm font-bold text-gray-900 dark:text-gray-100">{title}</span>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {state.changeEnds && <HeaderChip label="Change ends" icon={<Rotate3d className="w-3 h-3" />} />}
            {showDecidingChip && <HeaderChip label="Deciding set" />}
            {(!online || unsyncedCount > 0) && (
              <HeaderChip
                label={unsyncedCount > 0 ? `${unsyncedCount} unsynced` : 'Offline'}
                icon={!online ? <CloudOff className="w-3 h-3" /> : undefined}
              />
            )}
          </div>
        </div>

        <ScoreBoard state={state} ourName={ourName} opponentName={opponentName} />

        {!storageAvailable && (
          <p className="text-xs text-red-600 dark:text-red-400">
            This browser's storage is unavailable (private browsing?) — points won't survive a reload until synced.
          </p>
        )}

        <PointLogStrip points={points} onOpen={() => setShowPointLog(true)} />
      </div>

      {/* Split court: opponent pad, serve controls, our pad, stat tiles */}
      <div className="flex-1 min-h-0 flex flex-col gap-2 px-3 pt-1.5 pb-2.5">
        <PointPad
          side="them"
          name={opponentName}
          score={sideGameScore(state, 'them', format.noAd)}
          serving={state.server === 'them'}
          pressure={state.pressure.them}
          onTap={() => handleTap('them')}
        />
        <ServeControls
          serverName={serverName}
          netPlayerName={netPlayerName}
          onSwap={toggleNetPlayer}
          serveState={serveState}
          onFault={recordFault}
        />
        <PointPad
          side="us"
          name={ourName}
          score={sideGameScore(state, 'us', format.noAd)}
          serving={state.server === 'us'}
          pressure={state.pressure.us}
          onTap={() => handleTap('us')}
        />
        <StatTiles
          name={statsSide === 'us' ? ourName : opponentName}
          side={statsSide}
          stats={matchStats[statsSide]}
          onSwitch={() => setStatsSide((s) => (s === 'us' ? 'them' : 'us'))}
        />
      </div>

      {/* Footer */}
      <div
        className="flex-shrink-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-3 py-1.5 flex items-center justify-between"
        style={{ paddingBottom: 'calc(0.375rem + env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          onClick={undo}
          className="min-h-[48px] min-w-[48px] flex items-center gap-1.5 px-3 text-sm font-semibold text-gray-800 dark:text-gray-200 active:bg-gray-100 dark:active:bg-gray-800 rounded-lg"
          aria-label="Undo last point"
        >
          <Undo2 className="w-4 h-4" />
          Undo
        </button>
        <button
          type="button"
          onClick={() => setShowPointLog(true)}
          className="min-h-[48px] flex items-center gap-1.5 px-3 text-sm font-semibold text-gray-600 dark:text-gray-400 active:bg-gray-100 dark:active:bg-gray-800 rounded-lg"
        >
          <ListOrdered className="w-4 h-4" />
          Point log
        </button>
        <button
          type="button"
          onClick={() => setDarkMode((v) => !v)}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode (reduces glare outdoors)'}
          title={darkMode ? 'Light mode' : 'Dark mode'}
          className="min-h-[48px] min-w-[48px] flex items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 active:bg-gray-100 dark:active:bg-gray-800"
        >
          {darkMode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>

      <PointDetailsSheet
        open={showDetailsSheet}
        isProUser={isProUser}
        wonBy={lastWonBy}
        wonByName={lastWonBy === 'them' ? opponentName : ourName}
        scoreDelta={scoreDelta}
        showWinningPlayerPrompt={isDoubles && lastWonBy === 'us'}
        ourPlayers={ourPlayers}
        canBeAce={!!lastPoint && lastPoint.server === lastPoint.won_by}
        startExpanded={POINT_DETAIL_LEVEL === 'full'}
        autoSaveMs={autoSave.enabled ? autoSave.seconds * 1000 : null}
        onSave={handleSaveDetails}
        onSkip={handleSkipDetails}
        onRequirePro={handleRequirePro}
      />
      <PointLogSheet
        open={showPointLog}
        onClose={() => setShowPointLog(false)}
        points={points}
        format={format}
        isDoubles={isDoubles}
        ourName={ourName}
        opponentName={opponentName}
        ourPlayers={ourPlayers}
      />
      <UpgradeModal isOpen={showUpgradeModal} onClose={handleCloseModal} onUpgrade={handleUpgrade} />

      {state.winner && (
        <MatchSummaryOverlay
          winner={state.winner}
          sets={state.sets}
          ourName={ourName}
          opponentName={opponentName}
          points={points}
          format={format}
          isDoubles={isDoubles}
          ourPlayers={ourPlayers}
          onDone={() => navigate(`/games/${gameId}`)}
        />
      )}
    </div>
  );
};

function HeaderChip({ label, icon }: { label: string; icon?: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full whitespace-nowrap bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-400">
      {icon}
      {label}
    </span>
  );
}

/** One side's half of the court: the whole tile is the "point to this side" button, with the game score inside it. */
function PointPad({
  side,
  name,
  score,
  serving,
  pressure,
  onTap,
}: {
  side: Side;
  name: string;
  score: string;
  serving: boolean;
  pressure: PointPressure | null;
  onTap: () => void;
}) {
  const tone = side === 'us' ? 'bg-primary' : 'bg-slate-700 dark:bg-slate-800';
  return (
    <button
      type="button"
      onClick={onTap}
      aria-label={`Point to ${name}`}
      className={`flex-1 min-h-[96px] rounded-3xl ${tone} text-white px-4 py-3 flex flex-col justify-between text-left active:brightness-95 select-none`}
    >
      <span className="w-full flex items-center gap-2">
        {serving && <TennisBall className="w-3.5 h-3.5" />}
        <span className="text-[17px] font-bold truncate">{name}</span>
        {pressure && (
          <span className="ml-auto flex-shrink-0 text-[10px] font-extrabold tracking-widest uppercase bg-white/20 rounded-full px-2.5 py-1">
            {PRESSURE_LABEL[pressure]}
          </span>
        )}
      </span>
      <span className="w-full flex items-end justify-between">
        <span className="font-extrabold leading-[0.85] tabular-nums" style={{ fontSize: 'clamp(48px, 11vh, 96px)' }}>
          {score}
        </span>
        <span className="text-[11px] font-bold uppercase tracking-wider opacity-75">Tap for point</span>
      </span>
    </button>
  );
}

function StatTiles({ name, side, stats, onSwitch }: { name: string; side: Side; stats: SideStats; onSwitch: () => void }) {
  const tiles: Array<{ label: string; value: string }> = [
    { label: '1st In', value: stats.firstServePct !== null ? `${stats.firstServePct}%` : '—' },
    { label: 'Aces', value: String(stats.aces) },
    { label: 'Winners', value: String(stats.winners) },
    { label: 'Unforced', value: String(stats.unforcedErrors) },
    { label: 'Dbl Flt', value: String(stats.doubleFaults) },
  ];
  return (
    <div className="flex-shrink-0">
      <button
        type="button"
        onClick={onSwitch}
        aria-label={`Showing stats for ${name}. Switch side`}
        className="w-full flex items-center justify-between gap-2 min-h-[24px] text-[10px] font-bold tracking-wider uppercase"
      >
        <span className={`truncate ${side === 'us' ? 'text-primary' : 'text-slate-600 dark:text-slate-300'}`}>{name}</span>
        <span className="flex-shrink-0 text-gray-400 dark:text-gray-500">
          {stats.points} pts won · Tap to switch
        </span>
      </button>
      <div className="grid grid-cols-5 gap-1.5">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 py-1.5 flex flex-col items-center justify-center gap-0.5 min-w-0"
          >
            <span className="text-base font-bold text-gray-900 dark:text-gray-50 tabular-nums leading-tight">{t.value}</span>
            <span className="text-[9px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide leading-none text-center">{t.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TennisScoringScreen;
